import type {
  QuizPlayerAttempt,
  QuizPlayerControlsProps,
  QuizPlayerEndProps,
  QuizPlayerProgressProps,
  QuizPlayerQuestionProps,
  QuizPlayerSlots,
  QuizPlayerWelcomeProps,
} from './types';
import { useQuizPlayer, type UseQuizPlayerProps } from './use-quiz-player';
import { QuizPlayerControls } from './quiz-player-controls';
import { QuizPlayerEndScreen } from './screens/quiz-player-end';
import { QuizPlayerProgress } from './quiz-player-progress';
import { QuizPlayerQuestion } from './quiz-player-question';
import { QuizPlayerWelcomeScreen } from './screens/quiz-player-welcome';

import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const BASE_CLASS = 'mm-quiz-player';

const rootVariants = cva(
  `${BASE_CLASS} w-full flex flex-col gap-6 text-card-foreground relative`,
  {
    variants: {
      variant: {
        default:
          '*:data-question:rounded-xl *:data-question:bg-card *:data-question:shadow-xl *:data-question:p-6',
        contained: 'rounded-xl bg-card shadow-xl p-6',
        ghost: '',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export type QuizPlayerRootProps = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  attempts?: QuizPlayerAttempt[];
  variant?: 'default' | 'contained' | 'ghost';
  slots?: QuizPlayerSlots;
  slotProps?: {
    welcome?: Partial<QuizPlayerWelcomeProps>;
    end?: Partial<QuizPlayerEndProps>;
  };
  defaultScreen?: 'welcome' | 'quiz';
  onFeedback?: (value: number) => void;
};

export type QuizPlayerProps = React.ComponentProps<'div'> &
  UseQuizPlayerProps &
  VariantProps<typeof rootVariants> &
  QuizPlayerRootProps;

export function QuizPlayer({
  // controller props
  questions = [],
  events = {},
  validate,
  requireConfirm = false,
  displayIndex,
  defaultScreen,
  // own props
  title,
  description,
  attempts = [],
  variant = 'default',
  slots = {},
  slotProps = {},
  className,
  onFeedback,
  ...others
}: QuizPlayerProps) {
  const {
    currentQuestion,
    currentIndex,
    selectedAnswer,
    isAnswerValidated,
    isCorrect,
    score,
    screen,
    stats,
    handleAnswerSelection,
    handleConfirmAnswer,
    handleNext,
    handleTimeUp,
    handleStart,
    handleRestart,
    handleCancel,
    isTimerActive,
  } = useQuizPlayer({
    questions,
    events,
    attempts,
    validate,
    requireConfirm,
    defaultScreen,
  });

  const {
    WelcomeScreen = QuizPlayerWelcomeScreen as React.ElementType<QuizPlayerWelcomeProps>,
    EndScreen = QuizPlayerEndScreen as React.ElementType<QuizPlayerEndProps>,
    Progress = QuizPlayerProgress as React.ElementType<QuizPlayerProgressProps>,
    Question = QuizPlayerQuestion as React.ElementType<QuizPlayerQuestionProps>,
    Controls = QuizPlayerControls as React.ElementType<QuizPlayerControlsProps>,
  } = slots;

  const rootProps = {
    ...others,
    className: cn(rootVariants({ variant, className })),
    'data-slot': 'quiz-player',
    'data-variant': variant,
  };

  if (screen === 'welcome') {
    return (
      <div {...rootProps}>
        <WelcomeScreen
          {...slotProps.welcome}
          title={title}
          description={description}
          questions={questions}
          onStart={handleStart}
          onCancel={handleCancel}
        />
      </div>
    );
  }

  if (screen === 'end') {
    return (
      <div {...rootProps}>
        <EndScreen
          {...slotProps.end}
          score={score}
          totalQuestions={questions.length}
          stats={stats}
          onRestart={handleRestart}
          onFeedback={onFeedback}
        />
      </div>
    );
  }

  return (
    <div {...rootProps}>
      <Progress
        title={title}
        current={currentIndex + 1}
        total={questions.length}
        timeLimit={currentQuestion?.timeLimit}
        isTimerActive={isTimerActive}
        onTimeUp={handleTimeUp}
        onCancel={handleCancel}
      />

      {currentQuestion && (
        <Question
          title={title}
          question={currentQuestion.question}
          choices={currentQuestion.choices}
          selectedAnswer={selectedAnswer}
          isAnswerValidated={isAnswerValidated}
          correctAnswer={currentQuestion.correctAnswer}
          hint={currentQuestion.hint}
          onSelectAnswer={handleAnswerSelection}
          isCorrect={isCorrect}
          data-question={currentQuestion.id}
        />
      )}

      <Controls
        onCancel={handleCancel}
        onConfirm={handleConfirmAnswer}
        onNext={handleNext}
        selectedAnswer={selectedAnswer}
        isAnswerValidated={isAnswerValidated}
        isLastQuestion={currentIndex === questions.length - 1}
        requireConfirm={requireConfirm}
      />
    </div>
  );
}
